import * as angular from 'angular';
import $ from 'jquery';
import { debounce, isString } from 'lodash';

import { getInitialFlagValue, testFocusSupportsPreventScrollOption } from '@mode/shared/util-js';
import { nativeIntersectionObserverSupported, whenElementInViewport } from '~/lib/element-in-viewport';

import helper from '../../../plugins/ace/completion-providers/helper';
import { AceLanguage } from '../../../selectors/notebook.selectors';
import { setupCustomThemesForAce } from '@mode/shared/util-ace-editor';
import { FeatureFlag } from '@mode/shared/contract-common';
import tippy from 'tippy.js';
import { NotebookHighlightService } from '../../../plugins/ace/notebook-highlight.service';

export function modeNotebookAce(
  ace,
  $timeout: angular.ITimeoutService,
  $compile,
  notebookHighlightService: NotebookHighlightService
) {
  return {
    require: '?ngModel',
    restrict: 'AE',
    scope: {
      ngModel: '=',
      lang: '<',
      session: '<',
      readOnly: '<',
      secretKeys: '<',
      controlEvents: '=',
      onBlur: '&',
      onFocus: '&',
      onTextChanged: '&',
    },
    template: `
      <div class="mode-notebook-ace-editor" ng-class="{ 'no-selection': readOnly }"></div>
    `,
    link: function (
      scope: angular.IScope,
      element: angular.IAugmentedJQuery,
      attributes: angular.IAttributes,
      ngModel: angular.INgModelController
    ) {
      const editorElement = element[0].querySelector('.mode-notebook-ace-editor');
      const enableSecureCredentials = getInitialFlagValue(FeatureFlag.RegisterSecureCredentialsNotebooks);

      const wrappedInitializeEditor = () => {
        initializeEditor.call(this, scope, ngModel, editorElement, enableSecureCredentials);
      };

      /**
       * For large notebooks we can defer the instantiation of ace editor
       * instances by waiting for the element to become [near] visible within
       * the viewport
       */
      if (nativeIntersectionObserverSupported) {
        const inViewportHandle = whenElementInViewport(editorElement);

        // Disconnect the observer in the case that the scope is destroyed
        // before the element is scrolled into the viewport
        scope.$on('$destroy', inViewportHandle.disconnect);

        // Manually update the DOM with the initial code to be displayed until
        // ace editor is instantiated. This improves the scroll experience by
        // minimizing potential jumpiness experienced while instantiating the
        // editor; the initial element styles match the editor styles closely
        // enough that content shift is minimal
        const unbindWatchInitialEditorValue = scope.$watch('ngModel', (value) => {
          if (isString(value) && value) {
            editorElement.textContent = value;
          }
        });

        inViewportHandle
          // Ensure that the model watcher is removed before initializing
          // ace, otherwise the model value will blast the elements
          // created by ace.. resulting in obscure console errors
          .then(unbindWatchInitialEditorValue)
          // and init when the element becomes visible
          .then(() => {
            scope.$applyAsync(wrappedInitializeEditor);
          });
      } else {
        wrappedInitializeEditor();
      }
    },
  };

  /////////////////
  /////////////////

  function initializeEditor(
    scope: angular.IScope,
    ngModel: angular.INgModelController,
    editorElement: Element,
    enableSecureCredentials: boolean
  ) {
    setupCustomThemesForAce(ace);

    const editor = ace.edit(editorElement);

    editor.setOptions({
      tabSize: 2,
      maxLines: 100,
      behavioursEnabled: true,
      enableBasicAutocompletion: true,
      autoScrollEditorIntoView: false,
      highlightActiveLine: false,
      showPrintMargin: false,
      showGutter: true,
      useWorker: false,
    });

    editor.setTheme('ace/theme/light');

    // remove deprecation warning
    editor.$blockScrolling = Infinity;
    editor.renderer.$fontMetrics.setPolling(false);
    editor.renderer.setScrollMargin(10, 10);

    const focusSupportsPreventScroll = testFocusSupportsPreventScrollOption();
    if (focusSupportsPreventScroll) {
      editor.textInput.$focusScroll = 'browser';
    }

    editor.commands.removeCommand('find');
    editor.commands.removeCommand('gotoline');

    editor.commands.bindKey('ctrl-space', null);
    editor.commands.bindKey('ctrl-shift-space', null);
    editor.commands.bindKey('alt-space', null);

    editor.commands.bindKey('cmd-]', 'indent');
    editor.commands.bindKey('cmd-[', 'outdent');
    editor.commands.bindKey('Ctrl-P', 'golineup');

    editor.commands.bindKey('tab', function (editor) {
      const session = editor.getSession();
      const pos = editor.getCursorPosition();
      const line = session.getLine(pos.row);

      // More rules to implement
      // * can't tab past comment symbols
      if (
        // if at beginning of line
        pos.column === 0 ||
        // if all chars before are whitespace
        /^\s+$/.test(line.substring(0, pos.column)) ||
        // prev AND next char are white space or nothing
        (line.charAt(pos.column - 1).trim() === '' && line.charAt(pos.column + 1).trim() === '')
      ) {
        editor.indent();
      } else {
        editor.execCommand('startAutocomplete');
      }
    });

    editor.commands.addCommands([
      {
        name: 'nouppercase',
        bindKey: {
          win: 'Ctrl-U',
          mac: 'Ctrl-U',
        },
        exec() {
          return false;
        },
      },
      {
        name: 'nofindnext',
        bindKey: {
          win: 'Ctrl-K',
          mac: 'Ctrl-K',
        },
        exec() {
          return false;
        },
      },
      {
        name: 'bluronescape',
        bindKey: {
          win: 'esc',
          mac: 'esc',
        },
        passEvent: true,
        exec(editorElem) {
          editorElem.blur();
          scope.$evalAsync(() => scope.onBlur());
        },
      },
      // Since the following command will select another cell, blur
      {
        name: 'bluronrunandselect',
        bindKey: 'shift-enter',
        passEvent: true,
        exec() {
          editor.blur();
        },
      },
    ]);

    // setup autocomplete
    const codeCompleter: AceAjax.Completer = {
      identifierRegexps: [/[a-zA-Z_0-9.\@\$\-\u00A2-\uFFFF]/],
      getCompletions(
        currentEditor: AceAjax.Editor,
        session: AceAjax.IEditSession,
        pos: AceAjax.Position,
        prefix: string,
        callback: AceAjax.CompletionCallback
      ) {
        const code = session.getLine(pos.row);
        if (scope.session && scope.session.hasForm('complete')) {
          scope.session
            .forms('complete')
            .$submit({ code, cursor_position: pos.column })
            .then((results) => {
              const keys = enableSecureCredentials ? scope.secretKeys : [];
              callback(
                null,
                [...results.matches, ...keys].map((result) => ({
                  name: result,
                  value: result,
                  replacement: result,
                  completer: helper.getCompleter(),
                  suggestionType: 'python',
                  ...(keys.includes(result) &&
                    enableSecureCredentials && {
                      meta: 'secret',
                    }),
                }))
              );
            });
        } else {
          callback(null, []);
        }
      },
    };

    const langTools = ace.require('ace/ext/language_tools');
    const CstyleBehaviour = ace.require('ace/mode/behaviour/cstyle').CstyleBehaviour;
    const pythonMode = new (ace.require('ace/mode/python').Mode)();
    // method to create new Custom HighlightRules
    if (enableSecureCredentials) {
      $timeout(() => {
        notebookHighlightService.highlightSecrets(editor, scope.secretKeys);
      });
      scope.$watch('secretKeys', function (newSecrets: Array<string>, oldSecrets: Array<string>) {
        if (newSecrets?.length !== oldSecrets?.length) notebookHighlightService.highlightSecrets(editor, newSecrets);
      });
    }

    pythonMode.$behaviour = new CstyleBehaviour();
    langTools.setCompleters([codeCompleter]);
    editor.completers = [codeCompleter];

    editor.on('blur', () => {
      const undo = editor.getSession().getUndoManager();
      const isClean = undo.isClean();

      scope.$evalAsync(() => {
        scope.onBlur({ changed: !isClean });
        editor.getSession().setUndoManager(new ace.UndoManager());
      });
    });

    editor.on('focus', (e, ed) => {
      if (!ed.getReadOnly()) {
        scope.$evalAsync(scope.onFocus);
        editor.renderer.$fontMetrics.checkForSizeChanges();
      }
    });

    editor.on(
      'change',
      debounce(function () {
        editor.renderer.$fontMetrics.checkForSizeChanges();
      }, 200)
    );

    // this is borrowed from angular-ui/ui-ace implementation of
    // ngModel view setting based on ace change detection
    // https://github.com/angular-ui/ui-ace
    const onChange = function () {
      return function (e) {
        const newValue = editor.session.getValue();

        if (
          angular.isDefined(ngModel) &&
          newValue !== ngModel.$viewValue &&
          // HACK make sure to only trigger the apply outside of the
          // digest loop 'cause ACE is actually using this callback
          // for any text transformation !
          !scope.$$phase &&
          !scope.$root.$$phase
        ) {
          scope.$evalAsync(function () {
            ngModel.$setViewValue(newValue);
          });
        }

        scope.onTextChanged({ $text: newValue });
      };
    };

    editor.getSession().on('change', onChange());

    if (enableSecureCredentials) {
      const tippyInstance: any = tippy(editor.renderer.scroller, {
        theme: 'capra',
        allowHTML: true,
        placement: 'bottom',
        trigger: 'manual',
      });

      editor.on(
        'mousemove',
        debounce(function (e) {
          const pos = e.getDocumentPosition();
          const session = editor.getSession();
          const secretKeys = scope.secretKeys;
          let token;
          if (!token && pos.column > 0) {
            token = session.getTokenAt(pos.row, pos.column + 1);
          }
          if (token && token.type === 'mode_secret' && secretKeys.includes(token.value)) {
            scope.tokenValue = token.value;
            const compiledTooltipContent = $compile(
              '<div class="secret-tooltip"><capra-icon icon="icon-lock"></capra-icon><div><div class="text-label">Secret</div><div class="text">{{tokenValue}}</div><div></div>'
            )(scope);
            tippyInstance.setContent(compiledTooltipContent[0]);
            tippyInstance.setProps({
              getReferenceClientRect: () => ({
                width: 0,
                height: 16,
                top: e.clientY,
                bottom: e.clientY,
                left: e.clientX,
                right: e.clientX,
              }),
            });
            tippyInstance?.show();
          } else {
            tippyInstance?.hide();
          }
        }, 200)
      );
      editor.container.addEventListener(
        'mouseout',
        debounce(function () {
          tippyInstance?.hide();
        }, 200)
      );
    }

    // Model Binding
    if (angular.isDefined(ngModel)) {
      ngModel.$formatters.push(function (value) {
        if (angular.isUndefined(value) || value === null) {
          return '';
        } else if (angular.isObject(value) || angular.isArray(value)) {
          throw new Error('ui-ace cannot use an object or an array as a model');
        } else {
          return value;
        }
      });

      ngModel.$render = function () {
        editor.getSession().setValue(ngModel.$viewValue);
      };
    }

    if (scope.controlEvents) {
      scope.controlEvents.focus = function () {
        $timeout(() => {
          const wasFocused = editor.isFocused();
          if (!wasFocused) {
            editor.focus();
          }

          const session = editor.getSession();
          //Get the number of lines
          const count = session.getLength();
          //Go to end of the last line if not clicking on highlighted text
          if (!wasFocused && editor.getCopyText() === '') {
            editor.gotoLine(count, session.getLine(count - 1).length);
          }
        });
      };
      scope.controlEvents.blur = function () {
        const wasFocused = editor.isFocused();
        if (wasFocused) {
          $timeout(() => editor.blur());
        }
      };
    }

    scope.$watch('lang', function (language: AceLanguage) {
      if (angular.isDefined(language)) {
        editor.setOptions({
          showGutter: language !== AceLanguage.Markdown,
          enableBasicAutocompletion: language !== AceLanguage.Markdown,
        });

        switch (language) {
          case AceLanguage.Python:
            editor.getSession().setMode(pythonMode);
            break;
          case AceLanguage.Markdown:
          default:
            editor.getSession().setMode('ace/mode/' + language);
            break;
        }
      }
    });

    scope.$watch('readOnly', (readOnly) => {
      if (readOnly) {
        editor.setReadOnly(true);
      } else {
        editor.setReadOnly(false);
      }
    });

    scope.$on('$destroy', function () {
      $(editor.textInput.getElement()).off('keypress');
      editor.destroy();
    });
  }
}

modeNotebookAce.$inject = ['ace', '$timeout', '$compile', 'NotebookHighlightService'];
